import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Card,
  Tag,
  Descriptions,
  Alert,
  Space,
  message,
  Spin,
} from 'antd'
import {
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import backendApis from '../../utils/backendApis'
import commaNumber from 'comma-number'
import moment from 'moment'

const PremiumBrandStatusModal = ({
  showModal,
  setShowModal,
  registrationData,
  onStatusChange,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  const handleCancel = async () => {
    Modal.confirm({
      title: '프리미엄 브랜드관 신청 취소',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>정말 프리미엄 브랜드관 신청을 취소하시겠습니까?</p>
          <p className='text-sm text-gray-500 mt-2'>
            취소 후 재신청이 가능합니다.
          </p>
        </div>
      ),
      okText: '취소하기',
      okType: 'danger',
      cancelText: '돌아가기',
      onOk: async () => {
        setIsCancelling(true)
        try {
          const result =
            await backendApis.cancelSummerBlackFridayPremiumBrandRegistration()

          if (result?.status === 200) {
            message.success('프리미엄 브랜드관 신청이 취소되었습니다.')
            onStatusChange && onStatusChange('cancelled')
            setShowModal(false)
          } else {
            message.error(result?.message || '취소 중 오류가 발생했습니다.')
          }
        } catch (error) {
          message.error('취소 중 오류가 발생했습니다.')
        } finally {
          setIsCancelling(false)
        }
      },
    })
  }

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return {
          color: 'orange',
          icon: <ClockCircleOutlined />,
          text: '심사 중',
          description:
            '신청서를 검토 중입니다. 최대한 빠른 시일 내에 결과를 알려드리겠습니다.',
        }
      case 'approved':
        return {
          color: 'green',
          icon: <CheckCircleOutlined />,
          text: '선정 완료',
          description: '축하합니다! 프리미엄 브랜드관에 선정되었습니다.',
        }
      case 'rejected':
        return {
          color: 'red',
          icon: <CloseCircleOutlined />,
          text: '미선정',
          description: '아쉽게도 이번 프리미엄 브랜드관에 선정되지 않았습니다.',
        }
      case 'cancelled':
        return {
          color: 'gray',
          icon: <CloseCircleOutlined />,
          text: '취소됨',
          description: '신청이 취소되었습니다.',
        }
      default:
        return {
          color: 'default',
          icon: null,
          text: '알 수 없음',
          description: '',
        }
    }
  }

  const statusInfo = getStatusInfo(registrationData?.registrationStatus)

  return (
    <Modal
      visible={showModal}
      onCancel={() => setShowModal(false)}
      width={600}
      footer={null}
      title={
        <div className='flex items-center gap-2'>
          <CrownOutlined className='text-yellow-500 text-xl' />
          <span className='text-lg font-bold'>프리미엄 브랜드관 신청 현황</span>
        </div>
      }
    >
      <div className='space-y-4'>
        {/* 상태 표시 */}
        <Card className='text-center'>
          <div className='mb-4'>
            <div className='text-4xl mb-2'>{statusInfo.icon}</div>
            <Tag color={statusInfo.color} className='text-base px-4 py-1'>
              {statusInfo.text}
            </Tag>
          </div>
          <p className='text-gray-600'>{statusInfo.description}</p>
        </Card>

        {/* 신청 정보 */}
        <Descriptions bordered column={1} size='small'>
          <Descriptions.Item label='신청일시'>
            {moment(registrationData?.registrationDate).format(
              'YYYY년 MM월 DD일 HH:mm',
            )}
          </Descriptions.Item>
          <Descriptions.Item label='담당자'>
            {registrationData?.sellerInfo?.representorName}
          </Descriptions.Item>
          <Descriptions.Item label='연락처'>
            {registrationData?.sellerInfo?.contactNumber}
          </Descriptions.Item>
          <Descriptions.Item label='이메일'>
            {registrationData?.sellerInfo?.email}
          </Descriptions.Item>
          <Descriptions.Item label='선택 상품 수'>
            {commaNumber(registrationData?.selectedItemCount || 0)}개
          </Descriptions.Item>
          <Descriptions.Item label='광고비'>
            <span className='font-bold text-[#FF6C00]'>
              5,000,000원{' '}
              <span className='text-xs text-gray-600'>(VAT 포함)</span>
            </span>
          </Descriptions.Item>
        </Descriptions>

        {/* 심사 코멘트 (있는 경우) */}
        {registrationData?.reviewComment && (
          <Alert
            message='심사 결과'
            description={registrationData.reviewComment}
            type={
              registrationData?.registrationStatus === 'approved'
                ? 'success'
                : 'info'
            }
            showIcon
          />
        )}

        {/* 액션 버튼 */}
        <div className='flex justify-end gap-3'>
          {registrationData?.registrationStatus === 'pending' && (
            <Button danger loading={isCancelling} onClick={handleCancel}>
              신청 취소
            </Button>
          )}
          <Button type='primary' onClick={() => setShowModal(false)}>
            확인
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default PremiumBrandStatusModal
